import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Megaphone, Building2, Sparkles, Briefcase, ShoppingBag, BarChart3, Rocket, Users } from "lucide-react";
import BrandMarquee from "@/components/BrandMarquee";
import FAQSection from "@/components/FAQSection";
import CTASection from "@/components/CTASection";
import SectionLabel from "@/components/SectionLabel";
import ParallaxImage from "@/components/ParallaxImage";
import { AGENCY_FAQS, REGIONAL_LANGUAGES, IMAGES } from "@/data/content";
import { AUTH_LINKS } from "@/data/auth-links";

const SERVICES = [
  "Bulk influencer marketing",
  "Regional influencer campaigns",
  "UGC content creators",
  "Instagram influencer campaigns",
  "YouTube creator collaborations",
  "Product seeding campaigns",
  "Influencer management",
  "Campaign reporting",
];

const OFFERINGS = [
  { Icon: Rocket, t: "Bulk Influencer Marketing Execution", d: "Large-scale influencer campaigns across Tamil Nadu and India with nano, micro, macro and regional creators." },
  { Icon: Megaphone, t: "UGC Content Creation at Scale", d: "Authentic user-generated content for Meta Ads, Instagram Reels, testimonials and e-commerce creatives." },
  { Icon: Users, t: "Regional Influencer Expertise", d: "Tamil, Telugu, Malayalam and Kannada creators who build stronger audience trust and engagement." },
  { Icon: Briefcase, t: "End-to-End Campaign Management", d: "From outreach to negotiation, briefing, coordination, posting and reporting — we handle everything." },
  { Icon: Sparkles, t: "Faster Turnaround", d: "Faster creator onboarding, quick content approvals, smooth execution and real-time coordination." },
  { Icon: BarChart3, t: "Multi-Niche Creator Network", d: "Lifestyle, beauty, fashion, food, tech, fitness, parenting, real estate, education and entertainment." },
];

const PARTNERS = [
  "Digital Marketing Agencies",
  "Advertising Agencies",
  "Branding Agencies",
  "Creative Agencies",
  "PR Agencies",
  "Social Media Agencies",
  "Performance Marketing Teams",
  "Startups & D2C Brands",
];

export default function Agency() {
  return (
    <div data-testid="agency-page" className="bg-[#050505] text-[#F3E8E0]">
      {/* HERO */}
      <section className="relative pt-32 pb-20 md:pt-44 md:pb-28 overflow-hidden grain-bg">
        <div className="absolute top-20 right-0 w-[500px] h-[500px] bg-[#FF4A00] opacity-10 rounded-full blur-[180px] pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 md:px-10 relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              className="lg:col-span-8"
            >
              <SectionLabel className="mb-6">For Agencies</SectionLabel>
              <h1
                data-testid="agency-hero-title"
                className="font-display text-4xl md:text-6xl lg:text-[6.25rem] font-semibold tracking-tighter leading-[0.92]"
              >
                Scale your agency with{" "}
                <span className="italic text-[#FF4A00] font-medium">strategic influence.</span>
              </h1>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="lg:col-span-4"
            >
              <p className="text-lg text-[#A3A3A3] leading-relaxed">
                Incer helps agencies execute high-volume influencer marketing campaigns, UGC content
                production, creator sourcing and campaign management at scale — across India.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to={AUTH_LINKS.brandSignUp}
                  data-testid="agency-cta-signup"
                  className="inline-flex items-center gap-2 bg-[#FF4A00] text-white text-sm font-medium px-6 py-3 rounded-full hover:bg-[#E64300] transition-all active:scale-95"
                >
                  Sign up as an Agency <Briefcase className="w-4 h-4" />
                </Link>
              </div>
            </motion.div>
          </div>

          {/* Service Tags */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mt-16 flex flex-wrap gap-2"
            data-testid="agency-services"
          >
            {SERVICES.map((s, i) => (
              <span
                key={i}
                className="px-4 py-2 rounded-full border border-[#222] bg-[#0a0a0a] text-sm text-[#F3E8E0] hover:border-[#FF4A00] hover:text-[#FF4A00] transition-colors cursor-default"
              >
                {s}
              </span>
            ))}
          </motion.div>
        </div>
      </section>

      {/* WHY PARTNER */}
      <section className="py-24 md:py-32 border-t border-[#1a1a1a]" data-testid="agency-why">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
            <div className="lg:col-span-6">
              <SectionLabel className="mb-5">Why partner with us</SectionLabel>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tighter leading-[0.95]">
                Your extended influencer marketing team.
              </h2>
              <p className="mt-8 text-lg text-[#A3A3A3] leading-relaxed">
                At Incer, we help digital agencies, advertising agencies, PR agencies, branding agencies
                and marketing teams execute influencer marketing campaigns faster, smoother and more
                effectively. From creator sourcing to campaign execution, we become your white-label
                execution partner.
              </p>
            </div>
            <div className="lg:col-span-6 relative">
              <div className="relative aspect-[4/3] rounded-3xl overflow-hidden border border-[#1a1a1a]">
                <ParallaxImage src={IMAGES.agency1} alt="Agency team" className="absolute inset-0" intensity={0.16} />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent z-10" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT AGENCIES GET */}
      <section className="py-24 md:py-32 border-t border-[#1a1a1a] bg-[#070707]" data-testid="agency-offerings">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="mb-16 max-w-3xl">
            <SectionLabel className="mb-5">What you get</SectionLabel>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tighter leading-[0.95]">
              Everything an agency needs to scale influence.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {OFFERINGS.map((o, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                data-testid={`agency-offering-${i}`}
                className="card-glow group p-8 border border-[#1a1a1a] rounded-2xl bg-[#0a0a0a] hover:border-[#FF4A00]/40 transition-colors"
              >
                <div className="w-11 h-11 rounded-xl bg-[#FF4A00]/10 flex items-center justify-center mb-6 group-hover:bg-[#FF4A00]/20 transition-colors">
                  <o.Icon className="w-5 h-5 text-[#FF4A00]" />
                </div>
                <h3 className="font-display text-xl font-medium tracking-tight mb-2">{o.t}</h3>
                <p className="text-[#A3A3A3] leading-relaxed">{o.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* REGIONAL EXPERTISE */}
      <section className="py-24 md:py-32" data-testid="agency-regional">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="mb-16 max-w-3xl">
            <SectionLabel className="mb-5">South India expertise</SectionLabel>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tighter leading-[0.95]">
              Regional creator marketing,{" "}
              <span className="italic text-[#FF4A00]">done right.</span>
            </h2>
            <p className="mt-6 text-lg text-[#A3A3A3] max-w-2xl">
              Regional content creates deeper cultural connection and better conversion. We help agencies
              connect with creators who build stronger audience trust across South India.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {REGIONAL_LANGUAGES.map((lang, i) => (
              <motion.div
                key={lang.code}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                data-testid={`region-lang-${i}`}
                className="group relative p-8 border border-[#1a1a1a] rounded-2xl bg-[#0a0a0a] overflow-hidden hover:border-[#FF4A00] transition-colors"
              >
                <div className="absolute top-6 right-6 font-display text-7xl font-semibold text-[#FF4A00]/10 group-hover:text-[#FF4A00]/30 transition-colors">
                  {lang.code}
                </div>
                <div className="relative">
                  <div className="label-eyebrow mb-4">Region</div>
                  <div className="font-display text-3xl font-medium tracking-tight mb-2">{lang.name}</div>
                  <p className="text-sm text-[#A3A3A3] mt-3">{lang.note}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PARTNERS */}
      <section className="py-24 md:py-32 border-t border-[#1a1a1a] bg-[#070707]" data-testid="agency-partners">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <SectionLabel className="mb-5">Who benefits</SectionLabel>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tighter leading-[0.95]">
                Agencies that benefit from Incer.
              </h2>
            </div>
            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {PARTNERS.map((p, i) => (
                <div
                  key={i}
                  data-testid={`agency-partner-${i}`}
                  className="flex items-center gap-3 p-5 border border-[#1a1a1a] rounded-xl hover:border-[#FF4A00]/40 transition-colors"
                >
                  <Building2 className="w-5 h-5 text-[#FF4A00]" />
                  <span className="font-medium text-[#F3E8E0]">{p}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <CTASection
        testId="agency-cta"
        eyebrow="Agency partnership"
        title="Outsource execution. Keep the credit."
        description="Many agencies partner with Incer to manage creator sourcing, coordination, execution and reporting — so they can focus on growth."
        ctaText="Talk to our team"
      />
      <FAQSection items={AGENCY_FAQS} testId="agency-faq" />
    </div>
  );
}
